import { createSlice } from "@reduxjs/toolkit";
import { CampaignApplicationSlice, ScoreLevel, SortOptions } from "types/campaign/applications";
import { bookmarkApplciation, fetchApplication, fetchApplicationPosts, fetchCampaignApplications } from "./thunk";
import { ERROR } from "types";
import { defaultApplicationFilters } from "Components/campaigns/applications/ApplicationFilters";

const initialState: CampaignApplicationSlice = {
    loading: true,
    error: null,
    campaigns: {},
    applicationLoading: false,
    applicationError: null,
    applied_filter_id: null,
    filters:defaultApplicationFilters,
    posts: {},
    postsLoading: true
}

const campaignApplications = createSlice({
    name: 'campaignApplications',
    initialState,
    reducers: {
        updateApplicationFilters: (state, { payload }) => {
            state.filters = payload;
        },
        setApplicationSearchQeury: (state, action) => {
            state.filters.q = action.payload;
            state.filters.page = 1;
        },
        updateApplicationStatus: (state, action) => {
            const { campaignId, status, applicationId }: { campaignId: string, applicationId: string, status: 'pending' | 'accepted' | 'rejected' | 'processing' } = action.payload;

            const campaignData = state.campaigns[campaignId] ? { ...state.campaigns[campaignId] } : null;
            if (campaignData) {
                const campaignApps = { ...campaignData.applications }
                if (campaignApps[applicationId]) {
                    campaignApps[applicationId] = {
                        ...campaignApps[applicationId],
                        application: {
                            ...campaignApps[applicationId].application,
                            status
                        }
                    }
                    campaignData.applications = campaignApps;
                }

                const appLists = { ...campaignData.filtered_lists }

                for (let filterId of Object.keys(appLists)) {
                    const list = [...appLists[filterId].list]

                    for (let i in list) {
                        if (list[i].influencer.uuid === applicationId) {
                            list[i] = {
                                ...list[i],
                                status
                            }
                        }
                    }

                    appLists[filterId] = {
                        ...appLists[filterId],
                        list
                    };
                }

                campaignData.filtered_lists = appLists;

                state.campaigns = {
                    ...state.campaigns,
                    [campaignId]: campaignData
                }

            }

        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCampaignApplications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCampaignApplications.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                const filterId = action.payload.filterId;
                const campaignId = action.payload.campaignId;
                const existingData = state.campaigns?.[campaignId]?.filtered_lists?.[filterId]?.list ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.campaignApplications.items : [...existingData, ...action.payload.campaignApplications.items];
                state.campaigns[action.payload.campaignId] = {
                    ...state.campaigns[action.payload.campaignId],
                    filtered_lists: {
                        ...(state.campaigns[campaignId]?.filtered_lists ?? {}),
                        [filterId]: {
                            list: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.campaignApplications.count > finalList.length,
                            filters: action.payload.filters,
                            count: action.payload.campaignApplications.count
                        }
                    },
                    total: action.payload.campaignApplications.total
                }
                state.applied_filter_id = filterId;
            })
            .addCase(fetchCampaignApplications.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.error = action.payload || "Failed to fetch applications"
            })

            .addCase(fetchApplication.pending, (state) => {
                state.applicationLoading = true;
                state.applicationError = null;
            })

            .addCase(fetchApplication.fulfilled, (state, action) => {
                state.applicationLoading = false;
                state.applicationError = null;

                const { campaignId, application, influencerId } = action.payload;

                const existingApplications = state.campaigns[campaignId]?.applications ?? {};

                state.campaigns[campaignId] = {
                    ...state.campaigns[campaignId],
                    applications: {
                        ...existingApplications,
                        [influencerId]: {
                            application: application,
                            last_fetched_at: Date.now()
                        }
                    }
                }
            })
            .addCase(fetchApplication.rejected, (state, { payload }) => {
                state.applicationLoading = false;
                state.applicationError = payload || 'Failed to fetch application';
            })
            .addCase(bookmarkApplciation.fulfilled, (state, { payload }) => {
                const { campaignId, influencerId, bookmarked } = payload;

                const campaignData = state.campaigns[campaignId] ? { ...state.campaigns[campaignId] } : null;
                if (campaignData) {
                    const campaignApps = { ...campaignData.applications }
                    if (campaignApps[influencerId]) {
                        campaignApps[influencerId] = {
                            ...campaignApps[influencerId],
                            application: {
                                ...campaignApps[influencerId].application,
                                bookmarked
                            }
                        }
                        campaignData.applications = campaignApps;
                    }

                    const appLists = { ...campaignData.filtered_lists }

                    for (let filterId of Object.keys(appLists)) {
                        const list = [...appLists[filterId].list]

                        for (let i in list) {
                            if (list[i].influencer.uuid === influencerId) {
                                list[i] = {
                                    ...list[i],
                                    bookmarked
                                }
                            }
                        }

                        appLists[filterId] = {
                            ...appLists[filterId],
                            list
                        };
                    }

                    campaignData.filtered_lists = appLists;

                    state.campaigns = {
                        ...state.campaigns,
                        [campaignId]: campaignData
                    }

                }


            })
            .addCase(fetchApplicationPosts.pending, (state) => {
                state.postsLoading = true;
            })
            .addCase(fetchApplicationPosts.fulfilled, (state, {payload}) => {
                const { posts, campaignId, influencerId } = payload;

                state.posts[campaignId] = {
                    ...(state.posts[campaignId] ?? {}),
                    [influencerId] : {
                        ...posts,
                        last_fetched_at: Date.now()
                    }
                }
                state.postsLoading = false;

            })
            .addCase(fetchApplicationPosts.rejected, (state) => {
                state.postsLoading = false;
            })

    }
})

export const { updateApplicationFilters, updateApplicationStatus, setApplicationSearchQeury } = campaignApplications.actions;

export default campaignApplications.reducer;